import React, {useState, useEffect} from 'react';
import {useCookies} from 'react-cookie';
import {rootFixedColor} from 'styles/theme';
import {BsEye, BsEyeSlash} from 'react-icons/bs';
import {FcAbout} from 'react-icons/fc';
import styled from 'styled-components';
import {
  encryptSHA256,
  userLoginWebTest,
} from 'api/api';
import {useNavigate} from 'react-router-dom';
import {authEncryptAes256} from 'api/deployignore';

const MainContainer = styled.section`
  color: #f2f3f5;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  .title {
    font-family: var(--font-pretendard-bold);
    font-size: 1.25rem;
  }
  input[type='text'],
  input[type='password'] {
    padding: 0.5rem 1.25rem;
    height: 3rem;
    border-radius: 0.625rem;
    &:focus,
    &:focus-visible {
      outline-color: ${rootFixedColor.primaryColor};
    }
  }
  input[type='checkbox'] {
    appearance: none;
    width: 1.25rem;
    height: 1.25rem;
    background-color: #555555;
    border-radius: 0.25rem;
    &:checked {
      background-color: ${rootFixedColor.primaryColor};
    }
  }
  p {
    font-size: 12px;
  }
`;
const MainWrap = styled.main`
  display: flex;
  flex-direction: column;
  width: 360px;
  border-radius: 1rem;
  padding: 2rem 1rem;
  gap: 1.5rem;
  background-color: ${prop => prop.theme.layer2BgColor};
`;
const InputWrapColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  position: relative;
  > div {
    position: relative;
    display: flex;
    gap: 0.5rem;
  }
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
  .info:hover {
    cursor: pointer;
    & ~ p {
      display: block;
      position: absolute;
      right: 0;
      width: 64%;
      top: -0.5rem;
      background-color: #555555;
      border-radius: 0.625rem;
      padding: 0.25rem 0.5rem;
      &::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        margin: auto;
        left: -1.2rem;
        width: 1.2rem;
        height: 1.2rem;
        border: 0.625rem solid transparent;
        border-right-color: #555555;
      }
    }
  }
  .eye-on-off {
    position: absolute;
    right: 1rem;
    bottom: 0.75rem;
    svg {
      fill: #555555;
    }
    &:hover {
      cursor: pointer;
    }
  }
  p {
    color: #f9f9f9;
    display: none;
  }
`;
const InputWrapRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.625rem;
`;
const ButtonWrap = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  button {
    width: 50%;
    font-family: var(--font-pretendard-bold);
    font-size: 1rem;
    &:not(:disabled)&:hover {
      background-color: ${rootFixedColor.btnEditBgHoverColor};
    }
    &:disabled {
      background-color: ${rootFixedColor.secondaryColor};
    }
  }
`;
const DefaultBtn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background-color: ${rootFixedColor.primaryColor};
  color: ${rootFixedColor.rootTextColor};
`;
function UserLogin() {
  const navigate = useNavigate();
  // 비밀번호 입력값 보이기/가리기
  const [eyeOnOff, setEyeOnOff] = useState(false);
  function toggleOnOff() {
    setEyeOnOff(prev => !prev);
  }
  // 아이디/패스워드
  const [loginForm, setLoginForm] = useState({
    userId: '',
    userPassword: '',
  });
  const handleChangeValue = async (
    event: any,
  ) => {
    const {name, value} = event.target;
    if (name === 'userId') {
      setLoginForm(prevData => ({
        ...prevData,
        [name]: value,
      }));
    } else if (name === 'userPassword') {
      const encPassword = await encryptSHA256(value);
      setLoginForm(prevData => ({
        ...prevData,
        [name]: encPassword,
      }));
    }
  };
  // 로그인 함수
  const [isValid, setIsValid] = useState(false);
  const handleLogin = async () => {
    const random = '1'.repeat(32);
    // console.log('random:', random);
    const auth = authEncryptAes256(
      random,
      loginForm.userId,
      loginForm.userPassword,
    );
    // console.log('auth:', auth);
    const userLoginRequestDto = {
      auth: auth,
      os_type: 'W',
      push_token: 'web_test',
      app_ver: 'web_test',
      phone_model: 'web_test',
      os_ver: 'web_test',
    };
    const result = await userLoginWebTest(userLoginRequestDto);
    if ([409, 417, 418].includes(result.code)) {
      alert(result.message);
      return;
    } else if (result.code === 200) {
      // 로그인 성공 시 cookie에 아이디 저장
      const today = new Date();
      const expires = today.setDate(today.getDate() + 7);
      if (isRemember)
        setCookie('rememberUserId', loginForm.userId, {maxAge: expires});
      else removeCookie('rememberUserId');
      navigate('/user');
    } else {
      // 서버 에러가 그대로 표출되지 않도록, else 처리.
      alert(JSON.stringify(result));
      // alert('알 수 없는 에러가 발생했습니다. 관리자에게 문의해 주세요.');
      return;
    }
  };
  const handleKeyDown = async (
    event: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key === 'Enter' && isValid) handleLogin();
  };
  // 아이디 기억하기
  const [isRemember, setIsRemember] = useState(false);
  const [cookies, setCookie, removeCookie] = useCookies(['rememberUserId']);

  function handleRemember(e: React.ChangeEvent<HTMLInputElement>) {
    setIsRemember(e.target.checked);
  }
  // 쿠키에 저장되어 있으면 가져오기
  useEffect(() => {
    if (cookies.rememberUserId !== undefined) {
      setLoginForm({...loginForm, userId: cookies.rememberUserId});
      setIsRemember(true);
    }
  }, []);
  // 아이디/비밀번호가 다 입력됐을 때 로그인 버튼 활성화
  useEffect(() => {
    if (loginForm.userId && loginForm.userPassword) setIsValid(true);
    else setIsValid(false);
  }, [loginForm]);

  return (
    <>
      <MainContainer>
        {/* 로고 */}
        <figure>
          <img
            src="/image/FOCUSAI_Blue_Black.png"
            alt="logo"
            style={{height: '30px'}}
          />
        </figure>
        <MainWrap>
          <InputWrapColumn>
            <label htmlFor="loginId" className="title">
              ID
            </label>
            <input
              type="text"
              name="userId"
              id="loginId"
              value={loginForm.userId}
              onChange={handleChangeValue}
              placeholder="ID 입력"
              onKeyUp={handleKeyDown}
            />
          </InputWrapColumn>
          <InputWrapColumn>
            <div>
              <label htmlFor="loginPw" className="title">
                PASSWORD
              </label>
              <span className="info">
                <FcAbout />
              </span>
              <p>PASSWORD INFO</p>
            </div>
            <input
              type={!eyeOnOff ? 'password' : 'text'}
              name="userPassword"
              id="loginPw"
              onChange={handleChangeValue}
              placeholder="PASSWORD 입력"
              onKeyUp={handleKeyDown}
            />
            <div className="eye-on-off" onClick={toggleOnOff}>
              {!eyeOnOff ? <BsEyeSlash /> : <BsEye />}
            </div>
          </InputWrapColumn>
          <InputWrapRow>
            <input
              type="checkbox"
              id="saveId"
              checked={isRemember}
              onChange={e => handleRemember(e)}
            />
            <label htmlFor="saveId">ID 저장</label>
          </InputWrapRow>
          <ButtonWrap>
            <DefaultBtn type="button" onClick={handleLogin} disabled={!isValid}>
              LOG IN
            </DefaultBtn>
          </ButtonWrap>
        </MainWrap>
        <p>© 2025 FOCUS AI. ALL RIGHT RESERVED.</p>
      </MainContainer>
    </>
  );
}

export default UserLogin;
